import { useState, useEffect, useCallback, type FC } from "react";
import {
  CurrencyDollar,
  CoinsStacked01,
  Users01,
  Car01,
  Receipt,
  Tool01,
  Calendar,
  TrendUp01,
  TrendDown01,
} from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import PageLayout from "@/components/layout/PageLayout";
import { getDashboardStats, type DashboardStats } from "@/api/dashboard";
import { cx } from "@/utils/cx";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

function calcChange(current: number, previous: number): { pct: string; up: boolean | null } {
  if (previous === 0 && current === 0) return { pct: "0%", up: null };
  if (previous === 0) return { pct: "+100%", up: true };
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? "+" : "";
  return { pct: `${sign}${change.toFixed(0)}%`, up: change > 0 ? true : change < 0 ? false : null };
}

interface StatCardProps {
  title: string;
  value: string;
  icon: FC<{ className?: string }>;
  color: "brand" | "success" | "warning" | "gray" | "error";
  change?: { pct: string; up: boolean | null };
  changeLabel?: string;
}

function StatCard({ title, value, icon, color, change, changeLabel }: StatCardProps) {
  return (
    <div className="bg-bg-primary border border-border-secondary rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <FeaturedIcon icon={icon} color={color} size="md" theme="light" />
        {change && change.up !== null && (
          <span
            className={cx(
              "inline-flex items-center gap-1 text-xs font-medium",
              change.up ? "text-green-600" : "text-red-600"
            )}
          >
            {change.up ? (
              <TrendUp01 className="size-3.5" />
            ) : (
              <TrendDown01 className="size-3.5" />
            )}
            {change.pct}
          </span>
        )}
      </div>
      <p className="text-sm text-text-secondary mb-1">{title}</p>
      <p className="text-display-xs font-semibold text-text-primary">{value}</p>
      {changeLabel && (
        <p className="text-xs text-text-tertiary mt-1">{changeLabel}</p>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await getDashboardStats());
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const todayChange = stats ? calcChange(stats.revenueToday, stats.revenueYesterday) : null;
  const monthChange = stats ? calcChange(stats.revenueMonth, stats.revenueLastMonth) : null;
  const customerChange = stats ? calcChange(stats.customersThisMonth, stats.customersLastMonth) : null;

  return (
    <PageLayout
      title={`Welcome back, ${user?.firstName}`}
      subtitle="Here's an overview of your garage operations."
    >
      {loading ? (
        <div className="py-12 text-center text-text-tertiary">Loading dashboard...</div>
      ) : (
        <div className="space-y-6">
          {/* Revenue row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <StatCard
              title="Revenue Today"
              value={formatCurrency(stats?.revenueToday ?? 0)}
              icon={CurrencyDollar}
              color="brand"
              change={todayChange ?? undefined}
              changeLabel="vs yesterday"
            />
            <StatCard
              title="Revenue This Month"
              value={formatCurrency(stats?.revenueMonth ?? 0)}
              icon={CoinsStacked01}
              color="success"
              change={monthChange ?? undefined}
              changeLabel="vs last month"
            />
          </div>

          {/* Main stats row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            <StatCard
              title="Customers"
              value={String(stats?.customersCount ?? 0)}
              icon={Users01}
              color="brand"
              change={customerChange ?? undefined}
              changeLabel="new this month"
            />
            <StatCard
              title="Vehicles"
              value={String(stats?.vehiclesCount ?? 0)}
              icon={Car01}
              color="gray"
            />
            <StatCard
              title="Pending Invoices"
              value={String(stats?.pendingInvoices ?? 0)}
              icon={Receipt}
              color="warning"
            />
            <StatCard
              title="Active Jobs"
              value={String(stats?.activeJobs ?? 0)}
              icon={Tool01}
              color="brand"
            />
            <StatCard
              title="Today's Appointments"
              value={String(stats?.todayAppointments ?? 0)}
              icon={Calendar}
              color="success"
            />
          </div>
        </div>
      )}
    </PageLayout>
  );
}
